import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { openExternalLink } from "@/lib/standalone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageSpinner } from "@/components/ui/spinner";
import { SortScopeNote } from "@/components/ui/sort-scope-note";
import { formatDate } from "@/lib/utils";
import { dateSortValue } from "@/lib/table-sort";
import { Search, MessageSquare, BookOpen, Send, ExternalLink, FileText, ChevronLeft } from "lucide-react";

interface ArticleRow {
    id: string;
    title: string;
    body: string;
    source_uri: string | null;
    updated_at: string;
}

interface ManualPage {
    slug: string;
    title: string;
}

interface ManualPageDetail {
    slug: string;
    title: string;
    body: string;
}

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
}

export default function HelpPage() {
    const [tab, setTab] = useState<"articles" | "manual" | "chat">("articles");
    const [search, setSearch] = useState("");
    const [manualSearch, setManualSearch] = useState("");
    const [chatInput, setChatInput] = useState("");
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [openArticleId, setOpenArticleId] = useState<string | null>(null);
    const [openManualSlug, setOpenManualSlug] = useState<string | null>(null);

    // Sources from help_kb_articles, populated by the spec-library ingester
    // plus any office-authored runbooks. Search is case-insensitive
    // substring across title + body (Postgres `position(... in lower(...))`).
    const articles = useQuery({
        queryKey: ["help", "kb-articles", search],
        queryFn: () => {
            const p = new URLSearchParams({ limit: "50" });
            if (search) p.set("q", search);
            return api.get<{ articles: ArticleRow[]; total: number }>(`/api/help/kb-articles?${p}`);
        },
        enabled: tab === "articles",
    });

    const manualPages = useQuery({
        queryKey: ["help", "manual"],
        queryFn: () => api.get<{ pages: ManualPage[] }>("/api/help/manual"),
        enabled: tab === "manual",
    });

    const manualDetail = useQuery({
        queryKey: ["help", "manual", openManualSlug],
        queryFn: () => api.get<ManualPageDetail>(`/api/help/manual/${openManualSlug}`),
        enabled: !!openManualSlug,
    });

    const chatMutation = useMutation({
        mutationFn: (message: string) => api.post<{ answer: string }>("/api/help/chat", { message }),
        onSuccess: (data) => {
            setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
        },
        onError: () => {
            toast.error("Couldn't reach the help assistant. Please try again.");
        },
    });

    const handleChat = (e: React.FormEvent) => {
        e.preventDefault();
        if (!chatInput.trim()) return;
        const q = chatInput.trim();
        setMessages((m) => [...m, { role: "user", content: q }]);
        setChatInput("");
        chatMutation.mutate(q);
    };

    const openArticle = articles.data?.articles.find((a) => a.id === openArticleId);

    const articleCols: Column<ArticleRow>[] = [
        {
            key: "title",
            header: "Title",
            sortable: true,
            render: (r) => (
                <button
                    type="button"
                    className="font-medium text-left hover:text-primary"
                    onClick={() => setOpenArticleId(r.id)}
                >
                    {r.title}
                </button>
            ),
        },
        {
            key: "source_uri",
            header: "Source",
            render: (r) =>
                r.source_uri ? (
                    <button
                        type="button"
                        onClick={() => openExternalLink(r.source_uri!)}
                        className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
                    >
                        link <ExternalLink className="h-3 w-3" />
                    </button>
                ) : (
                    <span className="text-xs text-foreground/80">internal</span>
                ),
        },
        {
            key: "updated_at",
            header: "Updated",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.updated_at),
            firstSortDir: "desc",
            className: "w-32",
            render: (r) => <span className="text-xs text-foreground/80">{formatDate(r.updated_at)}</span>,
        },
    ];

    const filteredManualPages = (manualPages.data?.pages ?? []).filter(
        (p) => !manualSearch || p.title.toLowerCase().includes(manualSearch.toLowerCase()),
    );

    const tabs = [
        { key: "articles" as const, label: "Knowledge Base", icon: BookOpen },
        { key: "manual" as const, label: "Staff Manual", icon: FileText },
        { key: "chat" as const, label: "Ask AI", icon: MessageSquare },
    ];

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Help</h1>
                <p className="text-shell-foreground/80 text-sm">Knowledge base articles and AI-assisted answers.</p>
            </div>

            <div className="flex gap-1 border-b">
                {tabs.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors -mb-px ${
                            tab === key
                                ? "border-primary text-primary font-medium"
                                : "border-transparent text-shell-foreground/80 hover:text-shell-foreground"
                        }`}
                    >
                        <Icon className="h-4 w-4" />
                        {label}
                    </button>
                ))}
            </div>

            {tab === "articles" && (
                <>
                    <div className="flex items-center gap-3 max-w-2xl">
                        <div className="flex gap-2 flex-1">
                            <Input
                                placeholder="Search runbooks, manufacturer specs, IOMs, repair kits…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <Button variant="outline" size="icon">
                                <Search className="h-4 w-4" />
                            </Button>
                        </div>
                        {articles.data && (
                            <span className="text-xs text-shell-foreground/80 whitespace-nowrap">
                                {articles.data.total.toLocaleString()} articles indexed
                            </span>
                        )}
                    </div>
                    {articles.isLoading ? (
                        <PageSpinner />
                    ) : (
                        <DataTable
                            columns={articleCols}
                            data={articles.data?.articles ?? []}
                            keyField="id"
                            sortId="help.kb-articles"
                            loading={articles.isLoading}
                            emptyMessage="No articles found"
                        />
                    )}
                    <SortScopeNote
                        loaded={articles.data?.articles.length ?? 0}
                        total={articles.data?.total}
                        noun="articles"
                    />
                    {openArticle && (
                        <Card className="mt-2">
                            <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                                <div>
                                    <CardTitle className="text-base">{openArticle.title}</CardTitle>
                                    {openArticle.source_uri && (
                                        <button
                                            type="button"
                                            onClick={() => openExternalLink(openArticle.source_uri!)}
                                            className="text-xs text-primary inline-flex items-center gap-1 hover:underline mt-1 text-left"
                                        >
                                            {openArticle.source_uri} <ExternalLink className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => setOpenArticleId(null)}>
                                    Close
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <pre className="whitespace-pre-wrap text-xs leading-5 max-h-[60vh] overflow-y-auto">
                                    {openArticle.body}
                                </pre>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {tab === "manual" && (
                <>
                    {openManualSlug ? (
                        <div className="max-w-3xl">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="mb-3 -ml-2 text-shell-foreground/80"
                                onClick={() => setOpenManualSlug(null)}
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Back to Manual
                            </Button>
                            {manualDetail.isLoading ? (
                                <PageSpinner />
                            ) : manualDetail.data ? (
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle>{manualDetail.data.title}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <pre className="whitespace-pre-wrap text-sm leading-6 max-h-[70vh] overflow-y-auto font-sans">
                                            {manualDetail.data.body}
                                        </pre>
                                    </CardContent>
                                </Card>
                            ) : (
                                <p className="text-sm text-shell-foreground/80">Page not found.</p>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2 max-w-sm">
                                <Input
                                    placeholder="Search manual…"
                                    value={manualSearch}
                                    onChange={(e) => setManualSearch(e.target.value)}
                                />
                                <Button variant="outline" size="icon">
                                    <Search className="h-4 w-4" />
                                </Button>
                            </div>
                            {manualPages.isLoading ? (
                                <PageSpinner />
                            ) : filteredManualPages.length === 0 ? (
                                <p className="text-sm text-shell-foreground/80">
                                    {manualSearch ? "No pages match your search." : "Staff manual coming soon."}
                                </p>
                            ) : (
                                <div className="grid gap-2 max-w-2xl">
                                    {filteredManualPages.map((p) => (
                                        <Card
                                            key={p.slug}
                                            className="cursor-pointer hover:border-primary/60 transition-colors"
                                            onClick={() => setOpenManualSlug(p.slug)}
                                        >
                                            <CardContent className="flex items-center gap-3 py-3">
                                                <FileText className="h-4 w-4 text-primary shrink-0" />
                                                <span className="font-medium text-sm">{p.title}</span>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {tab === "chat" && (
                <Card className="max-w-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <MessageSquare className="h-5 w-5" />
                            AI Help Assistant
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                            {messages.length === 0 && (
                                <p className="text-sm text-foreground/80 text-center py-8">
                                    Ask a question about backflow operations, procedures, or compliance.
                                </p>
                            )}
                            {messages.map((msg, i) => (
                                <div
                                    key={i}
                                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`rounded-lg px-4 py-2 max-w-[80%] text-sm ${
                                            msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                                        }`}
                                    >
                                        {msg.content}
                                    </div>
                                </div>
                            ))}
                            {chatMutation.isPending && (
                                <div className="flex justify-start">
                                    <div className="rounded-lg px-4 py-2 bg-muted text-sm text-foreground/80">
                                        Thinking...
                                    </div>
                                </div>
                            )}
                        </div>
                        <form onSubmit={handleChat} className="flex gap-2">
                            <Textarea
                                placeholder="Ask a question..."
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleChat(e);
                                    }
                                }}
                                className="min-h-[40px] max-h-[120px]"
                                rows={1}
                            />
                            <Button type="submit" size="icon" disabled={chatMutation.isPending}>
                                <Send className="h-4 w-4" />
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
